import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbService } from '@card-software/library';
import { ApiResponse, AuthService } from '@card-software/nexus-utilities';
import { ProductionCardRoles } from '@app/models/roles/roles-group';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { ReportProxyService } from '@app/proxies/production/report.proxy.service';
import { WorkOrder } from '../../../../models/order/work-order';
import { DyeingColor } from '../../../../models/production/dyeing/dyeing-color';
import { DyeingForecastFormGroup } from '../../../../models/production/dyeing/dyeing-forecast-forms';
import { ProductionCard } from '../../../../models/production/production-card';
import { ProductionOrder } from '../../../../models/production/production-order';
import { ShipmentSegment } from '../../../../models/production/shipment-segment';
import { AssetProxyService } from '../../../../proxies/asset/asset.proxy.service';
import { LabDipRequestProxyService } from '../../../../proxies/item/lab-dip-request-proxy.service';
import { WorkOrderProxyService } from '../../../../proxies/order/work-order.proxy.service';
import { DyeingForecastService } from '../../../../services/dyeing-forecast.service';
import { ProductionCardService } from '../../../../services/production-card.service';
import { DyeingDetailsCardComponent } from '../../../dyeing/dyeing-details-card/dyeing-details-card.component';

@Component({
  selector: 'app-production-cards-details',
  templateUrl: './production-cards-details.component.html',
  styleUrl: './production-cards-details.component.scss',
})
export class ProductionCardsDetailsComponent implements OnInit {
  @ViewChild(DyeingDetailsCardComponent) dyeingDetailsCardComponent!: DyeingDetailsCardComponent;
  // toggle data options
  dataView: FormControl<'planning' | 'programming' | null> = new FormControl('planning');
  planningView: FormControl<'overview' | 'forecast' | null> = new FormControl('overview');
  planningOptions: { label: string; value: 'overview' | 'forecast' }[] = [];
  dataOptions: { label: string; value: 'planning' | 'programming' }[] = [];
  // Remove
  isLoading = 0;
  isUpdate = false;
  productionCards: ProductionCard | null = null;
  listProductionCards: ProductionCard[] = [];
  productOrder: ProductionOrder | null = null;
  shipmentSegments: ShipmentSegment[] = [];
  sortShipmentSegments: ShipmentSegment[] = [];
  totalProductOrder = signal(0);
  totalQtyProductionCards = signal(0);
  workOrder: WorkOrder | null = null;
  public forecastFilterOptions: { label: string; value: boolean }[] = [];

  //! handle forms
  createdDate: FormControl<Date | null> = new FormControl<Date | null>({ value: null, disabled: true });

  actualDeliveryDate: FormControl<Date | null> = new FormControl({
    value: null,
    disabled: true,
  });

  estimatedDeliveryDate: FormControl<Date | null> = new FormControl({
    value: null,
    disabled: true,
  });

  planningEndDate: FormControl<Date | null> = new FormControl({
    value: null,
    disabled: false,
  });
  /*   shipmentSegmentSelected: FormControl<ShipmentSegment | null> = new FormControl(null);
   */ filterForecastCreated = new FormControl<boolean>(false);

  // this methods is for the prise
  public blockBtnAddCard = signal<boolean>(false);

  // only methods for handle  roles
  isEditor = signal(false);
  isViewer = signal(false);

  disabledBtnByViewers = computed(() => this.isViewer());
  saveMenuItems: MenuItem[] = [];

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private _assetsService = inject(AssetProxyService);
  private productionCardService = inject(ProductionCardService);
  private _workOrderProxyService = inject(WorkOrderProxyService);
  private dyeingForecastService = inject(DyeingForecastService);
  private labDipRequestProxyService = inject(LabDipRequestProxyService);
  private reportProxyService = inject(ReportProxyService);
  private messageService = inject(MessageService);
  private productionCardsProxyService = inject(ProductionCardsProxyService);
  private translateService = inject(TranslateService);
  private breadcrumbService = inject(BreadcrumbService);

  constructor() {
    this.dataOptions = [
      { label: this.translateService.instant('Planning'), value: 'planning' },
      { label: this.translateService.instant('Programming'), value: 'programming' },
    ];
    this.planningOptions = [
      { label: this.translateService.instant('Overview'), value: 'overview' },
      { label: this.translateService.instant('Forecast'), value: 'forecast' },
    ];

    this.forecastFilterOptions = [
      { label: this.translateService.instant('Pending'), value: false },
      { label: this.translateService.instant('All'), value: true },
    ];

    // Initialize the form controls
    this.createdDate.setValue(new Date());
    this.actualDeliveryDate.setValue(null);
    this.estimatedDeliveryDate.setValue(null);
    this.planningEndDate.setValue(null);

    this.saveMenuItems = [
      {
        label: this.translateService.instant('Save and Close'),
        icon: 'pi pi-save',
        command: () => this.onSubmit(true),
      },
    ];
  }

  ngOnInit() {
    this.canEdit();

    this.activatedRoute.queryParams.subscribe((params) => {
      const workOrderId = params['workOrderId'];
      const id = params['id'];

      if (workOrderId && id) {
        this.initialLoad(workOrderId);
      } else {
        // Redirige al inicio
        this.router.navigate(['/']);
      }
    });
    this.productionCardsFormArray.valueChanges.subscribe(() => {
      this.calculateTotalQuantity();
    });
  }

  calculateTotalQuantity() {
    const total = this.productionCardsFormArray.controls.reduce((acc, card) => {
      const entries = card.get('productionCardEntries')?.value;
      if (entries) {
        const quantitySum = entries.reduce((sum: number, entry: { quantity?: number }) => sum + (entry.quantity || 0), 0);
        return acc + quantitySum;
      }
      return acc;
    }, 0);

    const totalRounded = total.toFixed(2);

    this.totalQtyProductionCards.set(parseFloat(totalRounded));
  }

  public addProductionCard(): void {
    this.productionCardService.addNewProductionCard();
  }
  canEdit() {
    if (AuthService.hasRole(ProductionCardRoles.ProductionCardsViewer)) {
      this.isViewer.set(true);
      this.applyViewMode();
    }
    if (AuthService.hasRole(ProductionCardRoles.ProductionCardsEditor)) {
      this.isEditor.set(true);
      this.isViewer.set(false);
    }
  }

  private initialLoad(workOrderId: string) {
    this.isLoading++;
    const assetsObservable = this._assetsService.getAllMachineOptionCorrectWay();
    const workOrderObservable: Observable<ApiResponse<WorkOrder>> = this._workOrderProxyService.getById(workOrderId);

    forkJoin([assetsObservable, workOrderObservable]).subscribe({
      next: ([assets, workOrder]) => {
        if (workOrder.data!.createdDate) {
          workOrder.data!.createdDate = new Date(workOrder.data!.createdDate);
        }
        this.workOrder = workOrder.data!;
        this.breadcrumbService.setCurrent(this.workOrder.orderNumber);

        // Obtener las production cards del work order
        this.productionCardsProxyService.getProductionCardsByWorkOrderIdList(workOrderId).subscribe({
          next: (response: ApiResponse<ProductionCard[]>) => {
            this.listProductionCards = response.data ?? [];
          },
          error: () => {
            this.listProductionCards = [];
          },
        });

        const colorsCallback = (colors: DyeingColor[]) => {
          this.dyeingForecastService.initialize(workOrder.data!, colors, assets.data!.items);
          this.productionCardService.initialize(workOrder.data!, colors, assets.data!.items);
          this.isLoading--;
        };

        if (workOrder.data!.orderType === 0 /* Sample */) {
          const colorIds = [
            ...new Set(
              workOrder
                .data!.fabrics.flatMap((f) => f.garmetDetails || [])
                .map((gd) => gd.colorReference)
                .filter((cr): cr is string => cr != null),
            ),
          ];
          this.labDipRequestProxyService.getColorsByIds(colorIds).subscribe({
            next: (colors) => colorsCallback(colors.data!),
            error: (error: HttpErrorResponse) => {
              console.log('Error loading colors by IDs:', error);
              this.isLoading--;
            },
          });
        } else {
          this.labDipRequestProxyService.getCustomerColors(workOrder.data!.customerReference).subscribe({
            next: (colors) => colorsCallback(colors.data!),
            error: (error: HttpErrorResponse) => {
              console.log('🚀 ~ ProductionCardsDetailsComponent ~ this.labDipRequestProxyService.getCustomerColors ~ error:', error);
              this.isLoading--;
            },
          });
        }
      },
      error: (error: HttpErrorResponse) => {
        console.log('🚀 ~ ProductionCardsDetailsComponent ~ forkJoin ~ error:', error);
        this.isLoading--;
        return;
      },
    });
  }

  get dyeingForecasts(): FormArray<FormGroup<DyeingForecastFormGroup>> {
    return this.dyeingForecastService.dyeingForecasts;
  }

  get productionCardsFormArray(): FormArray<FormGroup> {
    return this.productionCardService.productionCardsFormArray;
  }

  // #region Forecase Service
  addNewForecast() {
    this.dyeingForecastService.addNewForecast();
  }

  get dyeingGroups() {
    return this.dyeingForecastService.dyeingGroups;
  }

  get allAssets() {
    return this.dyeingForecastService.allAssets();
  }

  get allColors() {
    return this.dyeingForecastService.allColors();
  }

  get showOverview() {
    const garmentDetails = this.workOrder?.fabrics?.flatMap((f) => f.garmetDetails || []) || [];
    const uniqueColorReferences = new Set(garmentDetails.map((gd) => gd.colorReference).filter((cr) => cr != null));
    const distinctColorReferenceCount = uniqueColorReferences.size;

    //make sure all the colors in garment details are in the allColors list before returning true
    const allColorsPresent = Array.from(uniqueColorReferences).every((colorRef) => this.allColors.some((color) => color.id === colorRef));

    return distinctColorReferenceCount > 0 && distinctColorReferenceCount === this.allColors.length && allColorsPresent;
  }

  get filteredDyeingForecasts() {
    const filterValue = this.filterForecastCreated.value;
    return filterValue === false
      ? this.dyeingForecasts.controls.filter((fg) => fg.get('created')?.value === false)
      : this.dyeingForecasts.controls;
  }

  // #endregion

  showRightPanel = signal(true);
  toggleRightPanel() {
    this.showRightPanel.update((v) => !v);
  }

  onSubmit(navigateAway = false) {
    this.dyeingDetailsCardComponent.saveEstimatedDates();
    if (navigateAway) {
      this.router.navigate(['/work-orders-for-production-cards']);
    }
  }

  cancel() {
    this.router.navigate(['/work-orders-for-production-cards']);
  }

  getProductionCardReport() {
    if (!this.workOrder?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'There is not work order selected.',
        key: 'br',
      });
      return;
    }

    // Primero obtenemos las production cards actualizadas
    this.productionCardsProxyService
      .getProductionCardsByWorkOrderIdList(this.workOrder.id)
      .pipe(
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe({
        next: (response: ApiResponse<ProductionCard[]>) => {
          if (response.data && response.data.length > 0) {
            const productionCard = response.data[0];
            if (productionCard.id) {
              this.isLoading++;
              this.reportProxyService
                .getProductionCardReport(productionCard.id)
                .pipe(
                  finalize(() => {
                    this.isLoading--;
                  }),
                )
                .subscribe({
                  next: (response) => {
                    const blob = new Blob([response], { type: 'application/pdf' });
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = `ProductionCard-${productionCard.productionCardNumber}.pdf`;
                    link.click();
                    window.URL.revokeObjectURL(downloadUrl);
                    this.messageService.add({
                      severity: 'success',
                      summary: 'Exit',
                      detail: 'The report has been downloaded successfully!',
                      key: 'br',
                    });
                  },
                });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'There are not production cards available for this work order.',
              key: 'br',
            });
          }
        },
      });
  }

  applyViewMode() {
    this.planningEndDate.disable({ emitEvent: false });

    this.dyeingForecasts.controls.forEach((fg) => {
      fg.disable({ emitEvent: false });
    });
    this.productionCardsFormArray.controls.forEach((fg) => {
      fg.disable({ emitEvent: false });
    });
  }
}
